import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { PreOpRequirementsCard } from '../components/PreOpRequirementsCard';
import PostOpPrescriptionModal from '../components/PostOpPrescriptionModal';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';
import {
  surgeryService,
  type SurgicalProcedure,
  type SurgicalProcedureRequirement,
  type RequirementStatus,
  type PostOpPrescription,
  type InformedConsent,
  PROCEDURE_TYPE_LABELS,
  ANESTHESIA_LABELS,
  STATUS_LABELS,
  STATUS_BADGE,
} from '../surgeryService';
import { parseApiDate } from '../../../lib/format';

const DetailRow: React.FC<{ label: string; value?: string | number | null; full?: boolean }> = ({ label, value, full }) => (
  <div className={full ? 'col-12 mb-3' : 'col-md-6 mb-3'}>
    <div className="text-muted small mb-1">{label}</div>
    <div>{value || <span className="text-muted fst-italic">No registrado</span>}</div>
  </div>
);

const SurgicalProcedureDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [proc, setProc] = useState<SurgicalProcedure | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [requirements, setRequirements] = useState<SurgicalProcedureRequirement[]>([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);

  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [generatingPrescription, setGeneratingPrescription] = useState(false);
  const [prescription, setPrescription] = useState<PostOpPrescription | null>(null);
  const [prescriptionLoading, setPrescriptionLoading] = useState(false);

  const [generatingConsent, setGeneratingConsent] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    surgeryService.getById(Number(id))
      .then(setProc)
      .catch(() => setError('No se pudo cargar el procedimiento.'))
      .finally(() => setLoading(false));
  }, [id]);

  const fetchRequirements = async () => {
    if (!proc?.id) return;
    setRequirementsLoading(true);
    try {
      const data = await surgeryService.getRequirements(proc.id);
      setRequirements(data);
    } catch {
      setRequirements([]);
    } finally {
      setRequirementsLoading(false);
    }
  };

  const fetchPrescription = async () => {
    if (!proc?.id) return;
    setPrescriptionLoading(true);
    try {
      const data = await surgeryService.getPostOpPrescription(proc.id);
      setPrescription(data);
    } catch {
      setPrescription(null);
    } finally {
      setPrescriptionLoading(false);
    }
  };

  useEffect(() => {
    if (proc?.id) {
      fetchRequirements();
      fetchPrescription();
    }
  }, [proc?.id]);

  const handleGeneratePrescription = async () => {
    if (!proc?.id) return;
    setGeneratingPrescription(true);
    try {
      await surgeryService.generatePostOpPrescription(proc.id);
      setShowPrescriptionModal(true);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        await fetchPrescription();
        setShowPrescriptionModal(true);
      } else {
        alert(err?.response?.data?.message || 'No hay plantilla de receta post-operatoria para este tipo de procedimiento.');
      }
    } finally {
      setGeneratingPrescription(false);
    }
  };

  const handlePrescriptionSaved = () => {
    setShowPrescriptionModal(false);
    fetchPrescription();
  };

  const handlePrintPrescription = () => {
    window.open(`/surgery/${id}/post-op-prescription/print`, '_blank');
  };

  const handlePrintConsent = () => {
    window.open(`/surgery/${id}/informed-consent/print`, '_blank');
  };

  const handleStatusChange = async (requirementId: number, status: RequirementStatus) => {
    if (!proc?.id) return;
    await surgeryService.updateRequirementStatus(proc.id, requirementId, { status });
    await fetchRequirements();
  };

  const handleRegenerate = async () => {
    if (!proc?.id) return;
    await surgeryService.regenerateRequirements(proc.id);
    await fetchRequirements();
  };

  const requestDelete = () => {
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await surgeryService.delete(Number(id));
      setShowDeleteConfirm(false);
      navigate('/surgery');
    } catch {
      alert('Error al eliminar.');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (d?: string) =>
    d ? (parseApiDate(d) ?? new Date(NaN)).toLocaleDateString('es-DO', { day: '2-digit', month: 'long', year: 'numeric' }) : '-';

  if (loading) {
    return <Spinner variant="full-page" text="Cargando..." size="lg" />;
  }

  if (error || !proc) {
    return (
      <div className="alert alert-danger m-4">
        {error || 'Procedimiento no encontrado.'}
        <Link to="/surgery" className="btn btn-sm btn-outline-secondary ms-3">Volver</Link>
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">{proc.procedure_name}</h3>
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Cirugías', to: '/surgery' }, { label: 'Detalle' }]} />
          </div>
          <div className="col-auto d-flex gap-2">
            {proc.status === 'completed' && !prescription && !prescriptionLoading && (
              <button
                className="btn btn-primary"
                onClick={handleGeneratePrescription}
                disabled={generatingPrescription}
              >
                {generatingPrescription ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Generando...</>
                ) : (
                  <><FeatherIcon icon="file-text" size={14} className="me-1" /> Generar Receta Post-Operatoria</>
                )}
              </button>
            )}
            {proc.status === 'completed' && !!prescription?.items?.length && (
              <>
                <button
                  className="btn btn-outline-info"
                  onClick={() => setShowPrescriptionModal(true)}
                >
                  <FeatherIcon icon="edit-2" size={14} className="me-1" /> Ver/Editar Receta
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={handlePrintPrescription}
                >
                  <FeatherIcon icon="printer" size={14} className="me-1" /> Imprimir Receta
                </button>
              </>
            )}
            <button
              className="btn btn-outline-primary"
              onClick={handlePrintConsent}
              disabled={generatingConsent}
            >
              <FeatherIcon icon="file-text" size={14} className="me-1" /> Consentimiento Informado
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate(`/surgery/${id}/edit`)}
            >
              <FeatherIcon icon="edit-2" size={14} className="me-1" /> Editar
            </button>
            <button className="btn btn-outline-danger" onClick={requestDelete}>
              <FeatherIcon icon="trash-2" size={14} className="me-1" /> Eliminar
            </button>
          </div>
        </div>
      </div>

      {/* Header card */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h4 className="mb-0">{proc.patient?.first_name} {proc.patient?.last_name}</h4>
              {proc.patient?.identification && (
                <small className="text-muted">{proc.patient.identification}</small>
              )}
            </div>
            <div className="col-md-3 text-md-center">
              <div className="text-muted small">Fecha</div>
              <strong>{formatDate(proc.procedure_date)}</strong>
            </div>
            <div className="col-md-3 text-md-end">
              <span className={STATUS_BADGE[proc.status] ?? 'custom-badge status-grey'}>
                {STATUS_LABELS[proc.status] ?? proc.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Pre-Operative Requirements */}
      <PreOpRequirementsCard
        procedureId={proc.id}
        requirements={requirements}
        loading={requirementsLoading}
        onStatusChange={handleStatusChange}
        onRegenerate={handleRegenerate}
      />

      {/* Información del procedimiento */}
      <div className="card mb-3">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0"><i className="fa fa-info-circle me-2"></i>Información del Procedimiento</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <DetailRow label="Nombre del Procedimiento" value={proc.procedure_name} />
            <DetailRow label="Tipo" value={PROCEDURE_TYPE_LABELS[proc.procedure_type] ?? proc.procedure_type} />
            <DetailRow label="Tipo de Anestesia" value={proc.anesthesia_type ? ANESTHESIA_LABELS[proc.anesthesia_type] : undefined} />
            <DetailRow label="Duración" value={proc.duration_minutes ? `${proc.duration_minutes} minutos` : undefined} />
            <DetailRow
              label="Cirujano"
              value={proc.doctor ? `Dr. ${proc.doctor.first_name} ${proc.doctor.last_name}${proc.doctor.specialty ? ` — ${proc.doctor.specialty.name}` : ''}` : undefined}
            />
          </div>
        </div>
      </div>

      {/* Diagnóstico y técnica */}
      <div className="card mb-3">
        <div className="card-header bg-secondary text-white">
          <h5 className="mb-0"><i className="fa fa-stethoscope me-2"></i>Diagnóstico y Técnica</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <DetailRow label="Diagnóstico Preoperatorio" value={proc.diagnosis} full />
            {proc.technique && <DetailRow label="Técnica Quirúrgica" value={proc.technique} full />}
            {proc.findings && <DetailRow label="Hallazgos Intraoperatorios" value={proc.findings} full />}
          </div>
        </div>
      </div>

      {/* Postoperatorio */}
      {(proc.complications || proc.postop_indications || proc.notes) && (
        <div className="card mb-3">
          <div className="card-header" style={{ backgroundColor: '#17a2b8', color: 'white' }}>
            <h5 className="mb-0"><i className="fa fa-heartbeat me-2"></i>Postoperatorio</h5>
          </div>
          <div className="card-body">
            <div className="row">
              {proc.complications && <DetailRow label="Complicaciones" value={proc.complications} full />}
              {proc.postop_indications && <DetailRow label="Indicaciones Postoperatorias" value={proc.postop_indications} full />}
              {proc.notes && <DetailRow label="Notas Adicionales" value={proc.notes} full />}
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <Link to="/surgery" className="btn btn-outline-secondary">
          <FeatherIcon icon="arrow-left" size={14} className="me-1" /> Volver al listado
        </Link>
      </div>

      <PostOpPrescriptionModal
        procedureId={proc.id}
        open={showPrescriptionModal}
        generating={generatingPrescription}
        onClose={() => { setShowPrescriptionModal(false); setGeneratingPrescription(false); }}
        onSaved={handlePrescriptionSaved}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        title="Eliminar procedimiento quirúrgico"
        body="¿Eliminar este procedimiento quirúrgico? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default SurgicalProcedureDetailPage;
